import React, { useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Upload, X, FileText, AlertCircle, CheckCircle, Home } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

// Use dynamic backend URL to ensure same environment consistency
const getBackendURL = () => {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8001';
  }
  return `${window.location.protocol}//${window.location.host}`;
};
const BACKEND_URL = getBackendURL();

const SupportRequest = () => {
  const [formData, setFormData] = useState({
    client_name: '',
    business_name: '',
    email: '',
    mobile_phone: '',
    issue_description: '',
    urgency: ''
  });
  
  const [files, setFiles] = useState([]);
  const [businessNameStatus, setBusinessNameStatus] = useState(null); // null, 'checking', 'valid', 'invalid'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // null, 'success', 'error'
  const { toast } = useToast();

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Reset business name validation when business name changes
    if (field === 'business_name') {
      setBusinessNameStatus(null);
    }
  };

  const validateBusinessName = async () => {
    if (!formData.business_name.trim()) return;
    
    setBusinessNameStatus('checking');
    
    try {
      // Use the public business name validation endpoint
      const validationFormData = new FormData();
      validationFormData.append('business_name', formData.business_name);
      
      const response = await fetch(`${BACKEND_URL}/api/support/validate-business`, {
        method: 'POST',
        body: validationFormData
      });
      
      if (response.ok) {
        const result = await response.json();
        setBusinessNameStatus(result.valid ? 'valid' : 'invalid');
      } else {
        console.error('Business name validation failed:', response.status);
        setBusinessNameStatus('invalid'); // Fail closed for security
      }
    } catch (error) {
      console.error('Business name validation error:', error);
      setBusinessNameStatus('invalid'); // Fail closed for security
    }
  };

  // Simple similarity calculation (Jaccard similarity)
  const calculateSimilarity = (str1, str2) => {
    const set1 = new Set(str1.toLowerCase().split(''));
    const set2 = new Set(str2.toLowerCase().split(''));
    
    const intersection = new Set([...set1].filter(x => set2.has(x)));
    const union = new Set([...set1, ...set2]);
    
    return intersection.size / union.size;
  };

  const handleFileUpload = (event) => {
    const selectedFiles = Array.from(event.target.files);
    
    // Check file count
    if (files.length + selectedFiles.length > 5) {
      toast({
        title: "Too many files",
        description: "Maximum 5 files allowed",
        variant: "destructive",
      });
      return;
    }

    // Validate each file
    const validFiles = [];
    const allowedTypes = [
      'application/pdf',
      'application/msword',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/bmp'
    ];
    
    const allowedExtensions = ['.pdf', '.doc', '.docx', '.xls', '.xlsx', '.jpg', '.jpeg', '.png', '.bmp'];

    for (const file of selectedFiles) {
      // Check file size (5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast({
          title: "File too large",
          description: `${file.name} exceeds 5MB limit`,
          variant: "destructive",
        });
        continue;
      }

      // Check file type
      const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
      if (!allowedTypes.includes(file.type) && !allowedExtensions.includes(fileExtension)) {
        toast({
          title: "Invalid file type",
          description: `${file.name} is not a supported format`,
          variant: "destructive",
        });
        continue;
      }

      validFiles.push(file);
    }

    setFiles(prev => [...prev, ...validFiles]);
    event.target.value = ''; // Reset file input
  };

  const removeFile = (index) => {
    setFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.client_name || !formData.business_name || !formData.email || 
        !formData.mobile_phone || !formData.issue_description || !formData.urgency) {
      toast({
        title: "Missing required fields",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    // Validate business name is approved
    if (businessNameStatus === 'invalid') {
      toast({
        title: "Business not registered",
        description: "It seems your business is not registered for our ticketing system. Please contact us on 1300 234 972.",
        variant: "destructive",
      });
      return;
    }

    if (businessNameStatus !== 'valid') {
      toast({
        title: "Please validate business name",
        description: "Please enter your business name and wait for validation before submitting",
        variant: "destructive",
      });
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast({
        title: "Invalid email",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus(null);

    try {
      const submitFormData = new FormData();
      
      // Add form fields
      Object.keys(formData).forEach(key => {
        submitFormData.append(key, formData[key]);
      });

      // Add files
      files.forEach(file => {
        submitFormData.append('files', file);
      });

      const response = await fetch(`${BACKEND_URL}/api/support/tickets`, {
        method: 'POST',
        body: submitFormData
      });

      if (response.ok) {
        const result = await response.json();
        setSubmitStatus('success');
        
        // Reset form
        setFormData({
          client_name: '',
          business_name: '',
          email: '',
          mobile_phone: '',
          issue_description: '',
          urgency: ''
        });
        setFiles([]);
        setBusinessNameStatus(null);

        toast({
          title: "Support ticket created successfully!",
          description: `Your ticket ID is: ${result.id}. We will contact you within 24 hours.`,
        });

      } else {
        const error = await response.json();
        setSubmitStatus('error');
        toast({
          title: "Error creating support ticket",
          description: error.detail || "Please try again later",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error('Submit error:', error);
      setSubmitStatus('error');
      toast({
        title: "Network error",
        description: "Unable to submit request. Please check your connection and try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex flex-col items-center text-center">
            {/* Logos */}
            <div className="flex flex-col items-center gap-4">
              {/* Onsite Technology Logo - Much bigger */}
              <img 
                src="https://customer-assets.emergentagent.com/job_onsite-crm-1/artifacts/h18ui4w3_logo%20image.png"
                alt="Onsite Technology Solutions"
                className="h-40 w-auto object-contain"
              />
              
              {/* Medical IT Logo - Half the size of top logo */}
              <img 
                src="https://customer-assets.emergentagent.com/job_onsite-crm-1/artifacts/5ogpwrem_med%20it%20xero%20logo.png"
                alt="Medical IT"
                className="h-12 w-auto object-contain"
              />
            </div>
            
            {/* Header Text */}
            <div className="mt-4">
              <h1 className="text-2xl font-bold text-gray-900">Technical Support Request</h1>
              <p className="text-gray-600 mt-2">Submit your support request and our team will assist you</p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto p-6">
      
      <Card>
        <CardHeader>
          <CardTitle className="text-xl">Submit a Support Request</CardTitle>
          <p className="text-gray-600">
            Please fill out this form with your technical issue details. Our team will respond within 24 hours.
          </p>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Client Name */}
              <div>
                <Label htmlFor="client_name">Client Name *</Label>
                <Input
                  id="client_name"
                  type="text"
                  value={formData.client_name}
                  onChange={(e) => handleInputChange('client_name', e.target.value)}
                  placeholder="Client's full name"
                  required
                />
              </div>

              {/* Business Name */}
              <div>
                <Label htmlFor="business_name">Business Name *</Label>
                <div className="relative">
                  <Input
                    id="business_name"
                    type="text"
                    value={formData.business_name}
                    onChange={(e) => handleInputChange('business_name', e.target.value)}
                    onBlur={validateBusinessName}
                    placeholder="Business/company name"
                    required
                  />
                  {businessNameStatus === 'checking' && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                    </div>
                  )}
                  {businessNameStatus === 'valid' && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                    </div>
                  )}
                  {businessNameStatus === 'invalid' && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <AlertCircle className="h-4 w-4 text-red-600" />
                    </div>
                  )}
                </div>
                {businessNameStatus === 'invalid' && (
                  <Alert className="mt-2 border-red-200 bg-red-50">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-700">
                      It seems your business is not registered for our ticketing system. Please contact us on 1300 234 972.
                    </AlertDescription>
                  </Alert>
                )}
                {businessNameStatus === 'valid' && (
                  <Alert className="mt-2 border-green-200 bg-green-50">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <AlertDescription className="text-green-700">
                      Business name validated successfully.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Email */}
              <div>
                <Label htmlFor="email">Email Address *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                  placeholder="client.email@company.com"
                  required
                />
              </div>

              {/* Mobile Phone */}
              <div>
                <Label htmlFor="mobile_phone">Mobile Phone *</Label>
                <Input
                  id="mobile_phone"
                  type="tel"
                  value={formData.mobile_phone}
                  onChange={(e) => handleInputChange('mobile_phone', e.target.value)}
                  placeholder="0400 000 000"
                  required
                />
              </div>
            </div>

            {/* Issue Description */}
            <div>
              <Label htmlFor="issue_description">Issue Description *</Label>
              <Textarea
                id="issue_description"
                value={formData.issue_description}
                onChange={(e) => handleInputChange('issue_description', e.target.value)}
                placeholder="Please describe the technical issue in detail..."
                rows={4}
                required
              />
            </div>

            {/* Urgency */}
            <div>
              <Label>Urgency Level *</Label>
              <Select value={formData.urgency} onValueChange={(value) => handleInputChange('urgency', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select urgency level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="low">Low - Can wait a few days</SelectItem>
                  <SelectItem value="medium">Medium - Need response within 24 hours</SelectItem>
                  <SelectItem value="high">High - Urgent, affecting business operations</SelectItem>
                  <SelectItem value="critical">Critical - System down, immediate attention required</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* File Upload */}
            <div>
              <Label>Attachments (Optional)</Label>
              <div className="mt-2">
                <div className="flex items-center justify-center w-full">
                  <label htmlFor="file-upload" className="flex flex-col items-center justify-center w-full h-32 border-2 border-gray-300 border-dashed rounded-lg cursor-pointer bg-gray-50 hover:bg-gray-100">
                    <div className="flex flex-col items-center justify-center pt-5 pb-6">
                      <Upload className="w-8 h-8 mb-2 text-gray-500" />
                      <p className="mb-2 text-sm text-gray-500">
                        <span className="font-semibold">Click to upload</span> or drag and drop
                      </p>
                      <p className="text-xs text-gray-500">
                        PDF, Word, Excel, JPEG, BMP, PNG (Max 5 files, 5MB each)
                      </p>
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      className="hidden"
                      multiple
                      accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.bmp"
                      onChange={handleFileUpload}
                    />
                  </label>
                </div>

                {/* File List */}
                {files.length > 0 && (
                  <div className="mt-4 space-y-2">
                    {files.map((file, index) => (
                      <div key={index} className="flex items-center justify-between p-3 bg-gray-100 rounded-lg">
                        <div className="flex items-center space-x-3">
                          <FileText className="h-5 w-5 text-gray-500" />
                          <div>
                            <p className="text-sm font-medium text-gray-900">{file.name}</p>
                            <p className="text-xs text-gray-500">
                              {(file.size / 1024 / 1024).toFixed(2)} MB
                            </p>
                          </div>
                        </div>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          onClick={() => removeFile(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div className="flex gap-4">
              <Button
                type="submit"
                className="flex-1"
                disabled={isSubmitting || businessNameStatus === 'invalid'}
              >
                {isSubmitting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  'Submit Support Request'
                )}
              </Button>
            </div>

            {/* Success Message */}
            {submitStatus === 'success' && (
              <Alert className="border-green-200 bg-green-50">
                <CheckCircle className="h-4 w-4 text-green-600" />
                <AlertDescription className="text-green-700">
                  Support request submitted successfully! The client will receive a confirmation email shortly.
                </AlertDescription>
              </Alert>
            )}
          </form>
        </CardContent>
      </Card>
      </main>
      
      {/* Footer */}
      <footer className="bg-white border-t border-gray-200 mt-12">
        <div className="max-w-4xl mx-auto px-4 py-6 text-center">
          <p className="text-gray-600">
            Need immediate assistance? Call us on <strong>1300 234 972</strong>. If it is Critical or Urgent please log a ticket above OR call us - please do not email. If your business is not registered for support with us, please call us on <strong>1300 234 972</strong>.
          </p>
          <p className="text-sm text-gray-500 mt-2">
            © 2024 Onsite Technology Solutions.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default SupportRequest;